import moment from "moment";
import React from "react";
import appApi from "../../../lib/app-api";
import util from "../../../lib/util";
import LoadingPanel from "../../shared/loading-panel";
import { PTO_LEGEND_WEIGHT } from "../pto-consts";
import PtoButtonPanel from "./pto-button-panel";
import PtoDetailBox from "./pto-detail-box";
import PtoLinkLabel from "./pto-link-label";
import PtoMoreBox from "./pto-more-box";
import PtoStatusLabel from "./pto-status-label";
import { dateKey, itemClass, PTO_LEGEND_COLOR_MAP, VIEW_STATUS } from "./_common";

const _slotHeight = 26;
const _maxSlot = 2;
const _commonCellClass = "pto-calendar-cell";
const _commonHeaderCellClass = "pto-calendar-header-cell";
const _normalLeft = "0% 100%, 0% 0%";
const _normalRight = "100% 0%, 100% 100%";
const _arrowLeft = "10px 100%, 0% 50%, 10px 0%";
const _arrowRight = "calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%";

/**
 * @typedef {{
 *  startDate: import("moment").Moment,
 *  endDate: import("moment").Moment
 * }} DateRange
 * 
 * @typedef {{
 *  showWeekend: boolean,
 *  showStaffWithLeaveOnly: boolean,
 *  supervisorView: boolean
 * }} ViewSettings
 * 
 * @typedef {{
 *  viewSettings: ViewSettings,
 * }} PtoCalendarProps
 * 
 * @typedef {{
 *  date: import("moment").Moment,
 *  externalItems: object[],  
 *  hiddenExternals: object[],
 *  slots: boolean[],
 *  available: number
 * }} CellMetadata
 */

function _getDateList(dateRange, viewSettings) {

    const d = moment(dateRange.startDate);
    const result = []

    while (d.isSameOrBefore(dateRange.endDate, "D")) {
        if (viewSettings.showWeekend || (d.weekday() !== 0 && d.weekday() !== 6)) {
            result.push(moment(d));
        }
        d.add(1, "day");
    }

    return result;
}

/******************************** Item ********************************/

const CalendarWeekItem = (props) => {
    const { size, showSingle, viewSettings, item, dateRange, employee, onCancel, onApprove, onDeny, onConsultHR, date, deniableCodes, ...rest } = props;
    const typeCode = item.typeCode;
    const occupied = Math.max(1, props.occupiedSlots || 1);
    const overlapDegree = Math.max(1, props.overlapDegree || 1);
    const color = PTO_LEGEND_COLOR_MAP[typeCode].color;
    const lighterColor = util.pSBC(PTO_LEGEND_WEIGHT.Lighter, color);
    const borderColor = util.pSBC(PTO_LEGEND_WEIGHT.DarkerBorder, color);

    const itemTitle = item.title || (PTO_LEGEND_COLOR_MAP[item.typeCode] ? PTO_LEGEND_COLOR_MAP[item.typeCode].title : PTO_LEGEND_COLOR_MAP["MISC"].title);

    const total = (item.pto ? item.pto.unit : item.expenses.reduce((s, e) => s + e.unit, 0)) + " hr(s)";
    const mDate = moment(date);
    const shouldShowButtons = (() => {
        if (item.pto) {
            const d = moment(item.startDate);
             
            return item.pto.ptoLines && !item.pto.ptoLines.some(l => {
                const actionDate = moment(l.actionDate);
                return dateRange.startDate.isSameOrBefore(actionDate) && d.isAfter(actionDate);
            });
        }
        return false;
    })();

    if (size >= 2) {
        const [width, height, top, left] = [
            `calc(${item.length * 100}%)`,
            "100%",
            0,
            0
        ];

        return (
            <PtoDetailBox {...props} employee={employee} calendarItem={item}>
                <div style={{ width, height, flexGrow: size, display: "flex", flexDirection: "column", zIndex: 1 }}>
                    <div style={getContainerBorderStyle()}
                        className={itemClass(item)}
                        onMouseEnter={() => $(`.${itemClass(item)}`).addClass("pto-item-highlight")}
                        onMouseLeave={() => $(`.${itemClass(item)}`).removeClass("pto-item-highlight")}>

                        <div className="pto-week-view-item" style={getContainerStyle()}>
                            <PtoLinkLabel hidden={!!!item.previousDate || mDate.isSame(moment(item.previousDate), "week")} hideDate={true} date={item.allStartDate} direction="left" />
                            <div style={{ flexGrow: 1, height: "fit-content", alignSelf: "center" }}>
                                <div className="title">{itemTitle}</div>
                                <div className="totalHr">{total}</div>
                                <PtoStatusLabel pto={item.pto} />
                                {
                                    shouldShowButtons ?
                                        (
                                            overlapDegree === 1 ? <div style={{ marginTop: 20 }}>
                                                <PtoButtonPanel item={item} pto={item.pto} typeCode={typeCode} deniableCodes={deniableCodes}
                                                    onCancel={onCancel} onApprove={onApprove} onDeny={onDeny} onConsultHR={onConsultHR} />
                                            </div> :
                                                <PtoButtonPanel item={item} pto={item.pto} typeCode={typeCode} deniableCodes={deniableCodes}
                                                    onCancel={onCancel} onApprove={onApprove} onDeny={onDeny} onConsultHR={onConsultHR} />
                                        )
                                        : null
                                }


                            </div>
                            <PtoLinkLabel hidden={!!!item.nextDate || mDate.isSame(moment(item.nextDate), "week")} date={item.allEndDate} direction="right" />
                        </div>

                    </div>
                </div >
            </PtoDetailBox>
        );
    } else {
        const remain = item.length - Math.abs(mDate.diff(moment(item.startDate), "day"));
        const width = showSingle ? "100%" : `calc(${remain * 100}% + ${remain - 5}px)`;
        const flexGrow = overlapDegree == 2 ? 1 : undefined;

        return (
            <PtoDetailBox {...props} employee={employee} calendarItem={item}>
                <div title={`${item.title} - ${total}`} style={{ width: width, flexGrow, display: "flex", flexDirection: "column", height: `${_slotHeight}px%` }}>
                    <div style={getMiniContainerBorderStyle()}
                        className={itemClass(item)}>
                        <div className="pto-month-view-item" style={getMiniContainerStyle()}
                            onMouseEnter={() => $(`.${itemClass(item)}`).addClass("pto-item-highlight")}
                            onMouseLeave={() => $(`.${itemClass(item)}`).removeClass("pto-item-highlight")}>
                            <PtoLinkLabel hidden={!!!item.previousDate || mDate.isSame(moment(item.previousDate), "week")} hideDate={true} date={item.allStartDate} direction="left" />
                            <div className="title" style={{ width: 1, flexGrow: 1 }}>{itemTitle}</div>
                            {/*<div className="title" style={{ width: 1, flexGrow: 1, textAlign: "right", paddingRight: 5 }}>{item.typeCode}</div>*/}
                            <PtoStatusLabel hideLabel pto={item.pto} />
                            <PtoLinkLabel hidden={!!!item.nextDate || mDate.isSame(moment(item.nextDate), "week")} hideDate={item.length <= 1} date={item.allEndDate} direction="right" />
                        </div>
                    </div>
                </div >
            </PtoDetailBox>

        );
    }

    function getMiniContainerStyle() {
        const style = {
            backgroundColor: lighterColor,
            margin: 1,
            flexGrow: 1,
            display: "flex",
            flexDirection: "row"
        };

        let leftSide = _normalLeft;
        let rightSide = _normalRight;

        if (!showSingle) {
            if (item.previousDate) {
                leftSide = _arrowLeft;
            } else {
                style.marginLeft = 5;
            }

            if (item.nextDate) {
                rightSide = _arrowRight;
            }
        }

        style.clipPath = `polygon(${leftSide}, ${rightSide})`;

        return style;
    }

    function getMiniContainerBorderStyle() {
        const style = {
            backgroundColor: borderColor,
            margin: 2,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            zIndex: 2,
            height: _slotHeight,
            width: "100%"
        };

        let leftSide = _normalLeft;
        let rightSide = _normalRight;
        if (!showSingle) {
            if (item.previousDate) {
                leftSide = _arrowLeft;
            }

            if (item.nextDate) {
                rightSide = _arrowRight;
            }
        }
        style.clipPath = `polygon(${leftSide}, ${rightSide})`;

        return style;
    }

    function getContainerStyle() {
        const style = {
            backgroundColor: lighterColor,
            margin: 1,
            flexGrow: 1,
            display: "flex",
            flexDirection: "row"
        };

        let leftSide = _normalLeft;
        let rightSide = _normalRight;
        if (item.previousDate) {
            leftSide = _arrowLeft;
        } else {
            style.marginLeft = 5;
        }

        if (item.nextDate) {
            rightSide = _arrowRight;
        }

        style.clipPath = `polygon(${leftSide}, ${rightSide})`;

        return style;
    }

    function getContainerBorderStyle() {
        const style = {
            backgroundColor: borderColor,
            margin: 2,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
        };

        let leftSide = _normalLeft;
        let rightSide = _normalRight;

        if (item.previousDate) {
            leftSide = _arrowLeft;
        }

        if (item.nextDate) {
            rightSide = _arrowRight;
        }

        style.clipPath = `polygon(${leftSide}, ${rightSide})`;

        return style;
    }
}

/******************************** Grid ********************************/

class PtoGrid extends React.Component {

    constructor(props) {
        super(props);

        const items = props.items;
        const itemMap = new Map();
        if (items) {
            items.forEach(pto => {
                if (!itemMap.has(pto.peopleId)) {
                    itemMap.set(pto.peopleId, []);
                }

                itemMap.get(pto.peopleId).push(pto);
            });
        }

        this.state = {
            itemMap: itemMap
        };
    }


    /**
     * 
     * @param {DateRange} dateRange 
     * @param {ViewSettings} viewSettings 
     */
    _renderDayHeaders(dateRange, viewSettings) {
        let days = _getDateList(dateRange, viewSettings);

        const today = moment();

        return (
            [
                <div key="__" className={`${_commonHeaderCellClass} weekday`} style={{ gridRow: 1 }}></div>,
                ...(days.map(d => {
                    return (
                        <div key={d.format("ll")} style={{ gridRow: 1 }} className={`${_commonHeaderCellClass} weekday ${d.isSame(today, "day") ? "today" : ""}`}>
                            <p>{d.date()}</p>
                            <p>{d.format("dddd")}</p>
                        </div>
                    );
                }))
            ]
        );
    }

    _renderRow(employee, rowNum, employeeMap) {
        const props = this.props;
        const { viewSettings, dateRange, ...rest } = props;
        const itemMap = this.state.itemMap;
        const items = itemMap.get(employee.peopleId);
        
        const hiddenClass = viewSettings.showStaffWithLeaveOnly && !items ? "ap-hidden" : "";
        console.log('CALC total');
        const totalHr = items ? items.reduce((s, item) => s + item.expenses.reduce((s1, e) => s1 + e.unit, 0), 0) : 0;
        const subTextStyle = { fontWeight: "normal", fontSize: 11 };
        const result = [
            <div key="1" className={[_commonHeaderCellClass, hiddenClass].join(" ")}
                style={{ padding: 10, gridColumn: 1, gridRow: rowNum, textAlign: "Left", display: "flex", flexDirection: "column" }}>
                <div style={{ textAlign: "left" }}>
                    {viewSettings.supervisorView ?
                        (rowNum == 2 ? <span style={{ ...subTextStyle, color: "#666" }}>Supervisor</span> : <span style={{ ...subTextStyle, color: "#AAA" }}>Staff</span>) :
                        <span dangerouslySetInnerHTML={{ __html: "&nbsp;" }}></span>}
                </div>
                <div style={{ textAlign: "right", whiteSpace: "nowrap", fontSize: 12 }}>
                    {employee.fullName} <br />
                    <span style={{ ...subTextStyle, color: "#AAA" }}>{totalHr} hr(s)</span>
                </div>
            </div>
        ];

        const date = moment(dateRange.startDate);

        /** @type {Map<string, CellMetadata>} */
        const metadataMap = new Map();
        const overlapDegree = items && items.length > 0 ? _maxOverlapDegree(items) : 0;

        const itemsByDateMap = items ? items.reduce((m, v) => {
            const k = dateKey(v.startDate);
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(v);
            return m;
        }, new Map()) : new Map();
        let col = 2;

        while (date.isSameOrBefore(dateRange.endDate, "day")) {
            if (!viewSettings.showWeekend && (date.weekday() === 0 || date.weekday() === 6)) {
                date.add(1, "day");
                continue;
            }

            const key = dateKey(date);
            const divKey = `${employee.peopleId}_${key}`;
            if (!metadataMap.has(key)) metadataMap.set(key, _newMetadata(date, overlapDegree));
            const metadata = metadataMap.get(key);
            const itemsByDate = itemsByDateMap.get(key) || [];            
            const allItems = [...metadata.hiddenExternals, ...itemsByDate];
            allItems.sort((a, b) => a.length < b.length ? 1 : a.length > b.length ? -1 : 0);
            const drawn = [];
            const hidItems = [];

            allItems.forEach((v, i) => {
                if (i < metadata.available) drawn.push(allItems[i]);
                else hidItems.push(allItems[i]);
            });

            let slotIdx = 0;
            drawn.forEach(item => {
                const mStartDate = moment(item.startDate);
                const remain = item.length - Math.abs(date.diff(mStartDate, "day"));

                while (!metadata.slots[slotIdx]) slotIdx++;
                const d = moment(date);
                for (let i = 0; i < remain; i++) {
                    const k = dateKey(d);
                    if (k != key) {
                        if (!metadataMap.has(k)) metadataMap.set(k, _newMetadata(date, overlapDegree));
                        const m = metadataMap.get(k);
                        m.available -= 1;
                        m.externalItems.push(item);
                        m.slots[slotIdx] = false;
                    }
                    d.add(1, "day");
                }
                slotIdx++;
            })

            const itemSize = Math.max(1, Math.floor(_maxSlot / overlapDegree));

            hidItems.forEach(item => {
                const mStartDate = moment(item.startDate);
                const d = moment(date);
                const remain = item.length - Math.abs(date.diff(mStartDate, "day"));
                if (remain > 1) {
                    let sIdx = 0;
                    d.add(1, "day")
                    const k = dateKey(d);

                    if (!metadataMap.has(k)) metadataMap.set(k, _newMetadata(date, overlapDegree));
                    const m = metadataMap.get(k);

                    m.hiddenExternals.push(item);
                }
            });

            const moreItems = hidItems.length;
            const moreClass = hidItems.reduce((a, v, i) => { a.push(itemClass(v)); return a }, []);
            
            let itemIdx = 0;

            result.push(
                <div key={divKey} className={[_commonCellClass, hiddenClass].join(" ")} style={{ gridColumn: col, gridRow: rowNum, display: "flex", flexDirection: "column" }}>
                    {
                        metadata.slots.map((v, slotIdx) => {
                            if (v && itemIdx < drawn.length) {
                                const item = drawn[itemIdx++];
                                return <CalendarWeekItem size={itemSize} key={`${key}_${slotIdx}`} overlapDegree={overlapDegree}
                                    {...rest} item={item} dateRange={dateRange} date={moment(date)}
                                    viewSettings={viewSettings} employee={employee} />

                            } else {
                                return <div key={`${key}_${slotIdx}`} style={{ width: 1, height: _slotHeight + 4, zIndex: -1, flexGrow: (overlapDegree == 2 ? 1 : undefined) }}></div>
                            }
                        })
                    }
                    <PtoMoreBox items={[...metadata.externalItems, ...drawn, ...hidItems]}
                        count={moreItems} moreClass={moreClass} dateKey={dateKey(date)}
                        employeeMap={employeeMap} itemProps={props} />
                </div>
            );

            col++;
            date.add(1, "day");
        }

        return result;

        /**
         * 
         * @param {any[]} items 
         * @returns 
         */
        function _maxOverlapDegree(items) {
            const starts = items.map(item => moment(item.startDate));
            const ends = items.map(item => moment(item.startDate).add(item.length, "day"));

            let [iStart, iEnd] = [0, 0];

            starts.sort((mA, mB) => mA.isBefore(mB, "day") ? -1 : mA.isSame(mB, "day") ? 0 : 1);
            ends.sort((mA, mB) => mA.isBefore(mB, "day") ? -1 : mA.isSame(mB, "day") ? 0 : 1);

            let result = 0;
            let counter = 0;
            while (iStart < starts.length) {
                const [start, end] = [starts[iStart], ends[iEnd]];
                if (end.isSameOrBefore(start, "day")) {
                    counter--;
                    iEnd++;
                } else {
                    counter++;
                    iStart++;
                }
                result = Math.max(counter, result);
            }

            return result
        }

        function _newMetadata(date, overlapDegree) {
            const metadata = {
                date: moment(date),
                externalItems: [],
                slots: [],
                hiddenExternals: [],
                available: Math.min(overlapDegree, _maxSlot)
            };

            for (let i = 0; i < metadata.available; i++) metadata.slots.push(true);

            return metadata;
        }
    }

    render() {
        /** @type {PtoCalendarProps} */
        const props = this.props;
        const { dataset, dateRange, viewSettings, employees, ...rest } = props;
        const employeeMap = employees.reduce((m, e) => {
            m.set(e.peopleId, e);
            return m;
        }, new Map());
        const css = viewSettings.showWeekend ? "pto-week-7days-calendar" : "pto-week-5days-calendar";

        return (
            <div className={[css].join(" ")}>
                {this._renderDayHeaders(dateRange, viewSettings)}
                {employees.map((e, idx) => this._renderRow(e, idx + 2, employeeMap))}
            </div>
        );
    }
}


export default class PtoWeekCalendar extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            viewStatus: VIEW_STATUS.Loading,
            items: [],
            errorMessage: "",
        }
    }

    reload() {
        this.setState({
            viewStatus: VIEW_STATUS.Loading
        });
        this._initializeAsync();
    }

    componentDidMount() {
        this._initializeAsync();
    }

    async _initializeAsync() {
        const { employees, dateRange, ...rest } = this.props;
        const peopleIdList = employees.map(e => e.peopleId);
        const req = {
            peopleIdList,
            startDate: dateRange.startDate.format("YYYY-MM-DD"),
            endDate: dateRange.endDate.format("YYYY-MM-DD")
        };

        try {
            const res = await appApi.loadTimeOffItemByPeopleAndDateRangeAsync(req);
            const items = res.data.items || [];
            this.setState({
                items,
                viewStatus: VIEW_STATUS.Loaded
            })
        } catch (err) {
            this.setState({
                viewStatus: VIEW_STATUS.Error,
                errorMessage: "Error while loading PTO data."
            })
        }
    }

    _renderLoaded() {
        /** @type {PtoCalendarProps} */
        const props = this.props;
        const { dataset, dateRange, viewSettings, employees, ...rest } = props;


        return (
            <div className="pto-calendar-host">
                <PtoGrid {...props} items={this.state.items} />
            </div>
        );
    }

    _renderLoading() {
        return (
            <div className="text-center">
                <div>
                    <LoadingPanel message="Loading. Please wait..." />
                </div>
            </div>
        );
    }

    _renderError() {
        return (
            <div className="alert alert-danger text-left">
                {this.state.errorMessage}
            </div>
        );
    }

    render() {
        switch (this.state.viewStatus) {
            case VIEW_STATUS.Loaded:
                return this._renderLoaded();
            case VIEW_STATUS.Error:
                return this._renderError();
            default:
                return this._renderLoading();
        }
    }
}